import { useEffect, useState, type FormEvent } from "react";
import { useSearchParams } from "react-router";
import { BROWSE_SORTS, CONDITIONS } from "../../shared/limits.ts";
import type { BrowsePage } from "../../shared/types.ts";
import { EmptyState } from "../components/EmptyState.tsx";
import { ListingCard } from "../components/ListingCard.tsx";
import { api } from "../lib/api.ts";
import { syncClock } from "../lib/clock.ts";
import { useI18n } from "../lib/i18n.tsx";
import { useMeta } from "../lib/meta.ts";
import { useSegments } from "../lib/segments.ts";

/** /s — browse with tabs, filters, search, sort, pagination (R-BRW-1/2/3). */
export function Browse() {
  const { t, locale } = useI18n();
  const meta = useMeta();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") === "next_drop" ? "next_drop" : "live";
  const q = params.get("q") ?? "";
  const category = params.get("category") ?? "";
  const condition = params.get("condition") ?? "";
  const sort = (BROWSE_SORTS as readonly string[]).includes(params.get("sort") ?? "")
    ? params.get("sort")!
    : "default";
  const page = Math.max(1, Number(params.get("page")) || 1);
  const [data, setData] = useState<BrowsePage | null>(null);
  const [draftQ, setDraftQ] = useState(q);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const qs = params.toString();
  useEffect(() => {
    let live = true;
    api<BrowsePage>(`/api/browse?${qs}`)
      .then((d) => {
        if (!live) return;
        syncClock(d.server_ts);
        setData(d);
      })
      .catch(
        () => live && setData({ items: [], page: 1, page_size: 24, has_more: false, server_ts: 0 }),
      );
    return () => {
      live = false;
    };
  }, [qs]);

  const ids = tab === "live" ? (data?.items.map((i) => i.id) ?? []) : [];
  const segments = useSegments(ids);

  function set(next: Record<string, string>) {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) {
      if (v) p.set(k, v);
      else p.delete(k);
    }
    if (!("page" in next)) p.delete("page");
    setParams(p);
  }

  function search(e: FormEvent) {
    e.preventDefault();
    set({ q: draftQ.trim() });
  }

  const leaves = meta?.categories.filter((c) => c.parent_id !== null) ?? [];
  return (
    <div className="page">
      <form className="row" onSubmit={search} role="search">
        <input
          className="input"
          type="search"
          placeholder={t("common.search")}
          value={draftQ}
          onChange={(e) => setDraftQ(e.target.value)}
          data-testid="search"
        />
        <button className="btn" type="submit">
          {t("common.search")}
        </button>
        <button
          className="btn btn--ghost"
          type="button"
          onClick={() => setFiltersOpen((o) => !o)}
          aria-expanded={filtersOpen}
        >
          {t("common.filter")}
        </button>
      </form>
      <div className="chips" role="tablist">
        <button
          role="tab"
          aria-selected={tab === "live"}
          className={`chip${tab === "live" ? " is-on" : ""}`}
          onClick={() => set({ tab: "" })}
        >
          {t("browse.tab.live")}
        </button>
        <button
          role="tab"
          aria-selected={tab === "next_drop"}
          className={`chip${tab === "next_drop" ? " is-on" : ""}`}
          onClick={() => set({ tab: "next_drop" })}
        >
          {t("browse.tab.next_drop")}
        </button>
      </div>
      {filtersOpen ? (
        <div className="sheet" data-testid="filters">
          <label className="field">
            <span className="label">{t("sell.field.category")}</span>
            <select
              className="input"
              value={category}
              onChange={(e) => set({ category: e.target.value })}
            >
              <option value="">{t("common.all")}</option>
              {leaves.map((c) => (
                <option key={c.slug} value={c.slug}>
                  {locale === "vi" ? c.name_vi : c.name_en}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span className="label">{t("sell.field.condition")}</span>
            <select
              className="input"
              value={condition}
              onChange={(e) => set({ condition: e.target.value })}
            >
              <option value="">{t("common.all")}</option>
              {CONDITIONS.map((c) => (
                <option key={c} value={c}>
                  {t(`listing.condition.${c}`)}
                </option>
              ))}
            </select>
          </label>
          {tab === "live" ? (
            <label className="field">
              <span className="label">{t("browse.sort.default")}</span>
              <select
                className="input"
                value={sort}
                onChange={(e) => set({ sort: e.target.value === "default" ? "" : e.target.value })}
              >
                {BROWSE_SORTS.map((s) => (
                  <option key={s} value={s}>
                    {t(`browse.sort.${s}`)}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
        </div>
      ) : null}

      {data === null ? (
        <p className="muted">{t("common.loading")}</p>
      ) : data.items.length === 0 ? (
        <EmptyState
          text={q ? t("empty.search", { q }) : t("empty.browse")}
          cta={{ label: t("nav.home"), to: "/" }}
        />
      ) : (
        <>
          <div className="grid" data-testid="browse-grid">
            {data.items.map((c) => (
              <ListingCard
                key={c.id}
                card={c}
                {...(segments[c.id] ? { segment: segments[c.id] } : {})}
              />
            ))}
          </div>
          <div className="row row--between" style={{ marginTop: 16 }}>
            <button
              className="btn btn--ghost"
              disabled={page <= 1}
              onClick={() => set({ page: String(page - 1) })}
            >
              {t("common.back")}
            </button>
            <span className="muted small">{page}</span>
            <button
              className="btn btn--ghost"
              disabled={!data.has_more}
              onClick={() => set({ page: String(page + 1) })}
            >
              {t("common.next")}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
